import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Loader2,
  MessageSquareText,
  Pencil,
  Pin,
  PinOff,
  Plus,
  RefreshCcw,
  Search,
  SendHorizontal,
  ShieldCheck,
  Trash2,
  UsersRound
} from 'lucide-react'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { EmptyState } from '../components/ui/empty-state'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { useToast } from '../components/ui/toast'
import { useAuth } from '../hooks/useAuth'
import {
  AdminGroup,
  AdminGroupOwnerOption,
  AdminGroupPost,
  AdminGroupPayload,
  createAdminGroup,
  createAdminGroupPost,
  deleteAdminGroup,
  getAdminGroupOwnerOptions,
  getAdminGroupPosts,
  getAdminGroups,
  GroupType,
  pinAdminGroupPost,
  unpinAdminGroupPost,
  updateAdminGroup
} from '../lib/api/social'

const groupTypes: GroupType[] = ['Public', 'Private', 'InviteOnly']
const tourOperatorGroupSourceType = 'tour-operator-hike'

type GroupTypeFilter = 'All' | GroupType

type GroupFormState = {
  name: string
  description: string
  type: GroupType
  isLocationBased: boolean
  maxMembers: string
  ownerUserId: string
}

const initialForm: GroupFormState = {
  name: '',
  description: '',
  type: 'Public',
  isLocationBased: false,
  maxMembers: '',
  ownerUserId: ''
}

const formatDateTime = (value: string) => {
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(new Date(value))
  } catch (error) {
    console.warn('Failed to format group timestamp', error)
    return value
  }
}

const formatGroupType = (type: GroupType) => {
  if (type === 'InviteOnly') {
    return 'Invite only'
  }
  return type
}

const typeBadgeClass = (type: GroupType) => {
  switch (type) {
    case 'Public':
      return 'bg-emerald-50 text-emerald-700 ring-emerald-200'
    case 'Private':
      return 'bg-slate-100 text-slate-700 ring-slate-200'
    case 'InviteOnly':
      return 'bg-amber-50 text-amber-700 ring-amber-200'
  }
}

const formatPostAuthor = (post: AdminGroupPost) => post.postAsRole ?? post.author.username

const sortAdminPosts = (posts: AdminGroupPost[]) =>
  [...posts].sort((a, b) => {
    if (a.isPinned !== b.isPinned) {
      return a.isPinned ? -1 : 1
    }

    const aPinned = a.pinnedAt ? new Date(a.pinnedAt).getTime() : 0
    const bPinned = b.pinnedAt ? new Date(b.pinnedAt).getTime() : 0
    if (aPinned !== bPinned) {
      return bPinned - aPinned
    }

    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  })

const isTourOperatorGeneratedGroup = (group: AdminGroup) =>
  group.source?.type === tourOperatorGroupSourceType

export const SocialGroupsAdminPage = () => {
  const { user } = useAuth()
  const { addToast } = useToast()
  const [groups, setGroups] = useState<AdminGroup[]>([])
  const [ownerOptions, setOwnerOptions] = useState<AdminGroupOwnerOption[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [query, setQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState<GroupTypeFilter>('All')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [isGroupFormOpen, setIsGroupFormOpen] = useState(false)
  const [form, setForm] = useState<GroupFormState>(initialForm)
  const [formError, setFormError] = useState<string | null>(null)
  const [selectedPostGroup, setSelectedPostGroup] = useState<AdminGroup | null>(null)
  const [groupPosts, setGroupPosts] = useState<AdminGroupPost[]>([])
  const [groupPostTotal, setGroupPostTotal] = useState(0)
  const [loadingPosts, setLoadingPosts] = useState(false)
  const [savingPost, setSavingPost] = useState(false)
  const [pinningPostId, setPinningPostId] = useState<string | null>(null)
  const [postContent, setPostContent] = useState('')
  const [postError, setPostError] = useState<string | null>(null)
  const [isPostsOpen, setIsPostsOpen] = useState(false)
  const groupFormCardRef = useRef<HTMLDivElement>(null)
  const groupPostsCardRef = useRef<HTMLDivElement>(null)

  const role = (user?.role ?? 'Guest').toString()
  const isAdmin = role === 'Admin'

  const loadData = useCallback(
    async (filters?: { query?: string; type?: GroupTypeFilter }) => {
      setLoading(true)
      try {
        const nextQuery = filters?.query ?? query
        const nextType = filters?.type ?? typeFilter
        const [groupResponse, owners] = await Promise.all([
          getAdminGroups({
            query: nextQuery.trim() || undefined,
            type: nextType === 'All' ? undefined : nextType,
            limit: 100
          }),
          getAdminGroupOwnerOptions()
        ])
        setGroups(groupResponse.groups)
        setTotalCount(groupResponse.totalCount)
        setOwnerOptions(owners)
      } catch (error) {
        console.error('Failed to load social groups', error)
        addToast({
          title: 'Could not load groups',
          description: 'Please refresh or try again later.'
        })
      } finally {
        setLoading(false)
      }
    },
    [addToast, query, typeFilter]
  )

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false)
      return
    }
    void loadData()
  }, [isAdmin, loadData])

  useEffect(() => {
    if (editingId || form.ownerUserId || ownerOptions.length === 0) {
      return
    }
    setForm((prev) => ({ ...prev, ownerUserId: ownerOptions[0].userId }))
  }, [editingId, form.ownerUserId, ownerOptions])

  const stats = useMemo(() => {
    const publicGroups = groups.filter((group) => group.type === 'Public').length
    const uncappedGroups = groups.filter((group) => group.maxMembers == null).length
    return { publicGroups, uncappedGroups }
  }, [groups])

  const resetForm = () => {
    setForm({
      ...initialForm,
      ownerUserId: ownerOptions[0]?.userId ?? ''
    })
    setEditingId(null)
    setFormError(null)
  }

  const loadPostsForGroup = useCallback(
    async (group: AdminGroup) => {
      setLoadingPosts(true)
      try {
        const response = await getAdminGroupPosts(group.id, { limit: 50 })
        setGroupPosts(sortAdminPosts(response.posts))
        setGroupPostTotal(response.totalCount)
      } catch (error) {
        console.error('Failed to load group posts', error)
        addToast({
          title: 'Could not load posts',
          description: 'Please refresh or try again later.'
        })
      } finally {
        setLoadingPosts(false)
      }
    },
    [addToast]
  )

  const selectPostGroup = (group: AdminGroup) => {
    setSelectedPostGroup(group)
    setPostContent('')
    setPostError(null)
    setIsPostsOpen(true)
    void loadPostsForGroup(group)
    window.requestAnimationFrame(() => {
      groupPostsCardRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
    })
  }

  const parseMaxMembers = () => {
    const trimmed = form.maxMembers.trim()
    if (!trimmed) {
      return null
    }

    const parsed = Number(trimmed)
    if (!Number.isInteger(parsed) || parsed < 2) {
      throw new Error('Member cap must be at least 2, or blank for no cap.')
    }

    return parsed
  }

  const buildPayload = (): AdminGroupPayload => ({
    name: form.name.trim(),
    description: form.description.trim() || null,
    type: form.type,
    isLocationBased: form.isLocationBased,
    maxMembers: parseMaxMembers()
  })

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    void loadData({ query, type: typeFilter })
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!form.name.trim()) {
      setFormError('Group name is required.')
      return
    }

    if (!editingId && !form.ownerUserId) {
      setFormError('Choose an owner identity for the group.')
      return
    }

    let payload: AdminGroupPayload
    try {
      payload = buildPayload()
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Review the group details.')
      return
    }

    setFormError(null)
    setSaving(true)
    try {
      if (editingId) {
        const updated = await updateAdminGroup(editingId, payload)
        setGroups((prev) => prev.map((group) => (group.id === editingId ? updated : group)))
        setSelectedPostGroup((prev) => (prev?.id === updated.id ? updated : prev))
        addToast({ title: 'Group updated', description: 'Base Camp group changes are saved.' })
      } else {
        const created = await createAdminGroup({
          ...payload,
          ownerUserId: form.ownerUserId
        })
        setGroups((prev) => [created, ...prev])
        setTotalCount((prev) => prev + 1)
        addToast({ title: 'Group created', description: 'The group is available to the mobile app.' })
      }
      resetForm()
      setIsGroupFormOpen(false)
    } catch (error) {
      console.error('Failed to save group', error)
      const axiosError = error as { response?: { data?: { message?: string } } }
      const message = axiosError.response?.data?.message ?? 'Unable to save this group.'
      setFormError(message)
      addToast({ title: 'Save failed', description: message })
    } finally {
      setSaving(false)
    }
  }

  const startEdit = (group: AdminGroup) => {
    setEditingId(group.id)
    setForm({
      name: group.name,
      description: group.description ?? '',
      type: group.type,
      isLocationBased: group.isLocationBased,
      maxMembers: group.maxMembers?.toString() ?? '',
      ownerUserId: group.creatorId
    })
    setFormError(null)
    setIsGroupFormOpen(true)
    window.requestAnimationFrame(() => {
      groupFormCardRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
    })
  }

  const handleDelete = async (group: AdminGroup) => {
    const sourceWarning = isTourOperatorGeneratedGroup(group)
      ? ' It will not be recreated automatically.'
      : group.source
        ? ' This generated group may return the next time its source is synchronized.'
        : ''
    const confirmed = window.confirm(
      `Delete "${group.name}"? Its memberships, posts, and related group activity will be permanently removed.${sourceWarning}`
    )
    if (!confirmed) {
      return
    }

    setDeletingId(group.id)
    try {
      await deleteAdminGroup(group.id)
      setGroups((prev) => prev.filter((candidate) => candidate.id !== group.id))
      setTotalCount((prev) => Math.max(0, prev - 1))

      if (editingId === group.id) {
        resetForm()
        setIsGroupFormOpen(false)
      }
      if (selectedPostGroup?.id === group.id) {
        setSelectedPostGroup(null)
        setGroupPosts([])
        setGroupPostTotal(0)
        setIsPostsOpen(false)
      }

      addToast({ title: 'Group deleted', description: `${group.name} was removed from HikeStrong.` })
    } catch (error) {
      console.error('Failed to delete group', error)
      const axiosError = error as { response?: { data?: { message?: string } } }
      const message = axiosError.response?.data?.message ?? 'Unable to delete this group.'
      addToast({ title: 'Delete failed', description: message })
    } finally {
      setDeletingId(null)
    }
  }

  const handleCreateGroupPost = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!selectedPostGroup) {
      return
    }

    const content = postContent.trim()
    if (!content) {
      setPostError('Post content is required.')
      return
    }

    setPostError(null)
    setSavingPost(true)
    try {
      const created = await createAdminGroupPost(selectedPostGroup.id, { content })
      setGroupPosts((prev) => sortAdminPosts([created, ...prev]))
      setGroupPostTotal((prev) => prev + 1)
      setPostContent('')
      addToast({ title: 'Post published', description: 'The official group post is live.' })
    } catch (error) {
      console.error('Failed to publish group post', error)
      const axiosError = error as { response?: { data?: { message?: string } } }
      const message = axiosError.response?.data?.message ?? 'Unable to publish this post.'
      setPostError(message)
      addToast({ title: 'Publish failed', description: message })
    } finally {
      setSavingPost(false)
    }
  }

  const handleTogglePin = async (post: AdminGroupPost) => {
    if (!selectedPostGroup) {
      return
    }

    setPinningPostId(post.id)
    try {
      const updated = post.isPinned
        ? await unpinAdminGroupPost(selectedPostGroup.id, post.id)
        : await pinAdminGroupPost(selectedPostGroup.id, post.id)
      setGroupPosts((prev) => sortAdminPosts(prev.map((candidate) => (candidate.id === updated.id ? updated : candidate))))
      addToast({
        title: updated.isPinned ? 'Post pinned' : 'Post unpinned',
        description: updated.isPinned ? 'Pinned posts appear first in the mobile group feed.' : 'The post returned to chronological order.'
      })
    } catch (error) {
      console.error('Failed to update pinned state', error)
      addToast({
        title: 'Pin update failed',
        description: 'Unable to update this post.'
      })
    } finally {
      setPinningPostId(null)
    }
  }

  if (!isAdmin) {
    return (
      <Card className="border-dashed border-amber-500/40 bg-amber-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="h-5 w-5" />
            Admin Only
          </CardTitle>
          <CardDescription>
            Groups can only be managed by Admin accounts. Contact an admin if you need access.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-wide text-muted-foreground">Social</p>
          <h1 className="text-3xl font-semibold">Groups</h1>
          <p className="text-sm text-muted-foreground">
            Review the communities members see in the mobile Feed and Groups tab.
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={() => void loadData()} title="Reload groups">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm">
        <span><strong className="text-base text-foreground">{totalCount}</strong> total groups</span>
        <span><strong className="text-base text-foreground">{stats.publicGroups}</strong> public in this view</span>
        <span><strong className="text-base text-foreground">{stats.uncappedGroups}</strong> without a member cap</span>
      </div>

      <Card ref={groupFormCardRef} className="scroll-mt-4 shadow-sm">
        <CardHeader className="flex-row items-center justify-between space-y-0 gap-4">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2 text-lg">
              {editingId ? <Pencil className="h-5 w-5 text-primary" /> : <Plus className="h-5 w-5 text-primary" />}
              {editingId ? 'Edit group' : 'Add group'}
            </CardTitle>
            <CardDescription>
              {editingId ? 'Update the selected group.' : 'Create a mobile community using an admin owner identity.'}
            </CardDescription>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label={isGroupFormOpen ? 'Collapse add group' : editingId ? 'Continue editing group' : 'Open add group'}
            aria-expanded={isGroupFormOpen}
            aria-controls="group-form-panel"
            onClick={() => setIsGroupFormOpen((open) => !open)}
          >
            {isGroupFormOpen ? 'Collapse' : editingId ? 'Continue editing' : 'Open'}
            {isGroupFormOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </CardHeader>
        {isGroupFormOpen && (
          <CardContent id="group-form-panel" className="border-t border-border pt-4">
            <form className="space-y-4" onSubmit={handleSubmit}>
              <label className="block space-y-1">
                <span className="text-sm font-medium">Name</span>
                <Input
                  data-testid="group-admin-name-input"
                  value={form.name}
                  maxLength={100}
                  onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                  placeholder="Tour du Mont Blanc Training"
                />
              </label>

              <label className="block space-y-1">
                <span className="text-sm font-medium">Description</span>
                <Textarea
                  value={form.description}
                  maxLength={2000}
                  onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                  placeholder="Community for hikers training toward this goal."
                  rows={4}
                />
              </label>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block space-y-1">
                  <span className="text-sm font-medium">Type</span>
                  <select
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    value={form.type}
                    onChange={(event) => setForm((prev) => ({ ...prev, type: event.target.value as GroupType }))}
                  >
                    {groupTypes.map((type) => (
                      <option key={type} value={type}>
                        {formatGroupType(type)}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block space-y-1">
                  <span className="text-sm font-medium">Member cap</span>
                  <Input
                    inputMode="numeric"
                    value={form.maxMembers}
                    onChange={(event) => setForm((prev) => ({ ...prev, maxMembers: event.target.value }))}
                    placeholder="No cap"
                  />
                </label>
              </div>

              {!editingId && (
                <label className="block space-y-1">
                  <span className="text-sm font-medium">Owner identity</span>
                  <select
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    value={form.ownerUserId}
                    onChange={(event) => setForm((prev) => ({ ...prev, ownerUserId: event.target.value }))}
                  >
                    {ownerOptions.map((owner) => (
                      <option key={owner.userId} value={owner.userId}>
                        {owner.label} - {owner.role}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <label className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.isLocationBased}
                  onChange={(event) => setForm((prev) => ({ ...prev, isLocationBased: event.target.checked }))}
                />
                Location-based group
              </label>

              {formError && (
                <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {formError}
                </p>
              )}

              <div className="flex flex-wrap gap-2">
                <Button type="submit" disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  {editingId ? 'Save changes' : 'Create group'}
                </Button>
                {editingId && (
                  <Button type="button" variant="outline" onClick={resetForm} disabled={saving}>
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        )}
      </Card>

      <Card ref={groupPostsCardRef} className={selectedPostGroup ? 'scroll-mt-4 shadow-sm' : 'scroll-mt-4 border-dashed shadow-sm'}>
        <CardHeader className="flex-row items-center justify-between space-y-0 gap-4">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2 text-lg">
              <MessageSquareText className="h-5 w-5 text-primary" />
              Group posts
            </CardTitle>
            <CardDescription>
              {selectedPostGroup
                ? `Publish and pin official updates for ${selectedPostGroup.name}.`
                : 'Choose Posts in the table to manage official updates for a group.'}
            </CardDescription>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label={isPostsOpen ? 'Collapse group posts' : 'Open group posts'}
            aria-expanded={isPostsOpen}
            aria-controls="group-posts-panel"
            onClick={() => setIsPostsOpen((open) => !open)}
          >
            {isPostsOpen ? 'Collapse' : 'Open'}
            {isPostsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </CardHeader>
        {isPostsOpen && (
          <CardContent id="group-posts-panel" className="border-t border-border pt-4">
            {!selectedPostGroup ? (
              <EmptyState
                icon={MessageSquareText}
                title="Choose a group"
                description="Use the Posts action in the table below to load that group's updates."
              />
            ) : (
              <div className="space-y-4">
                <form className="space-y-3" onSubmit={handleCreateGroupPost}>
                  <label className="block space-y-1">
                    <span className="text-sm font-medium">Official post</span>
                    <Textarea
                      data-testid="group-admin-post-content"
                      value={postContent}
                      maxLength={2000}
                      onChange={(event) => setPostContent(event.target.value)}
                      placeholder="Share an official update with this group."
                      rows={4}
                    />
                  </label>
                  {postError && (
                    <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                      {postError}
                    </p>
                  )}
                  <Button type="submit" disabled={savingPost}>
                    {savingPost ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizontal className="h-4 w-4" />}
                    Publish post
                  </Button>
                </form>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">Recent posts</span>
                    <span className="text-muted-foreground">{groupPostTotal}</span>
                  </div>
                  {loadingPosts ? (
                    <div className="flex items-center justify-center rounded-md border border-border py-8 text-sm text-muted-foreground">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading posts
                    </div>
                  ) : groupPosts.length === 0 ? (
                    <EmptyState
                      icon={MessageSquareText}
                      title="No posts yet"
                      description="Publish the first official update for this group."
                    />
                  ) : (
                    <div className="space-y-2">
                      {groupPosts.map((post) => (
                        <div key={post.id} className="rounded-md border border-border p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                                {formatPostAuthor(post)}
                                {post.isAdminAuthored && (
                                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                                    Official
                                  </span>
                                )}
                                {post.isPinned && (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700 ring-1 ring-amber-200">
                                    <Pin className="h-3 w-3" />
                                    Pinned
                                  </span>
                                )}
                              </div>
                              <div className="mt-1 text-xs text-muted-foreground">{formatDateTime(post.createdAt)}</div>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => void handleTogglePin(post)}
                              disabled={pinningPostId === post.id}
                            >
                              {pinningPostId === post.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : post.isPinned ? (
                                <PinOff className="h-4 w-4" />
                              ) : (
                                <Pin className="h-4 w-4" />
                              )}
                              {post.isPinned ? 'Unpin' : 'Pin'}
                            </Button>
                          </div>
                          <p className="mt-3 whitespace-pre-wrap text-sm text-foreground">
                            {post.content || 'No text content'}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      <Card className="shadow-sm">
        <CardHeader className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <UsersRound className="h-5 w-5 text-primary" />
                Groups
              </CardTitle>
              <CardDescription>Find a group, then manage its posts, details, or deletion.</CardDescription>
            </div>
          </div>
          <form className="grid gap-3 md:grid-cols-[1fr_180px_auto]" onSubmit={handleSearch}>
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by name or description"
              aria-label="Search groups"
            />
            <select
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value as GroupTypeFilter)}
              aria-label="Filter groups by type"
            >
              <option value="All">All types</option>
              {groupTypes.map((type) => (
                <option key={type} value={type}>
                  {formatGroupType(type)}
                </option>
              ))}
            </select>
            <Button type="submit" variant="outline">
              <Search className="h-4 w-4" />
              Search
            </Button>
          </form>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Loading groups
            </div>
          ) : groups.length === 0 ? (
            <EmptyState
              icon={UsersRound}
              title="No groups found"
              description="Add a group or adjust your filters."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[960px] text-left text-sm">
                <thead className="border-b text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-4 font-medium">Group</th>
                    <th className="py-2 pr-4 font-medium">Type</th>
                    <th className="py-2 pr-4 font-medium">Members</th>
                    <th className="py-2 pr-4 font-medium">Owner</th>
                    <th className="py-2 pr-4 font-medium">Updated</th>
                    <th className="py-2 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {groups.map((group) => (
                    <tr key={group.id} className="align-top">
                      <td className="py-3 pr-4">
                        <div className="font-medium text-foreground">{group.name}</div>
                        <div className="mt-1 line-clamp-2 max-w-md text-xs text-muted-foreground">
                          {group.description || 'No description'}
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs font-medium text-primary">
                          {group.isLocationBased && <span>Location based</span>}
                          {group.source && (
                            <div className="inline-flex flex-wrap items-center gap-1.5">
                              <span>Generated from {group.source.sourceName || group.source.type}</span>
                              {isTourOperatorGeneratedGroup(group) && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                  aria-label={`Delete ${group.name}`}
                                  disabled={deletingId === group.id}
                                  onClick={() => void handleDelete(group)}
                                >
                                  {deletingId === group.id ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-3.5 w-3.5" />
                                  )}
                                  Delete
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${typeBadgeClass(group.type)}`}>
                          {formatGroupType(group.type)}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="font-medium">{group.memberCount}</div>
                        <div className="text-xs text-muted-foreground">
                          {group.maxMembers == null ? 'No cap' : `Cap ${group.maxMembers}`}
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2 font-medium">
                          <ShieldCheck className="h-4 w-4 text-primary" />
                          {group.creatorLabel}
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {group.creatorEmail ?? group.creatorUsername ?? group.creatorId}
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-xs text-muted-foreground">
                        {formatDateTime(group.updatedAt)}
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            aria-label={`Manage posts for ${group.name}`}
                            onClick={() => selectPostGroup(group)}
                          >
                            <MessageSquareText className="h-4 w-4" />
                            Posts
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            aria-label={`Edit ${group.name}`}
                            onClick={() => startEdit(group)}
                          >
                            <Pencil className="h-4 w-4" />
                            Edit
                          </Button>
                          {!isTourOperatorGeneratedGroup(group) && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                              aria-label={`Delete ${group.name}`}
                              disabled={deletingId === group.id}
                              onClick={() => void handleDelete(group)}
                            >
                              {deletingId === group.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                              Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
